import React, { useState, useEffect } from 'react';
import authService from 'services/authService';
import providerService from 'services/providerService';
import Swal from 'sweetalert2';
import { Layout } from 'components/layout';
import 'styles/provider.css';
import 'styles/manager.css';
import "./ProfileView.css"
export const ProfileView = () => {
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState(null);
    const [providerInfo, setProviderInfo] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const [formData, setFormData] = useState({
        nombre: '',
        email: '',
        telefono: '',
        direccion: ''
    });
    const [passwordForm, setPasswordForm] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });

    useEffect(() => {
        loadProfile();
    }, []);

    const loadProfile = async () => {
        try {
            setLoading(true);

            // Cargar datos del usuario
            const response = await authService.getProfile();

            if (!response.error) {
                const userData = response.data.data;
                setUser(userData);

                setFormData({
                    nombre: userData.nombre || '',
                    email: userData.email || '',
                    telefono: userData.telefono || '',
                    direccion: userData.direccion || ''
                });

                // Si es proveedor, cargar información adicional del proveedor
                if (userData.rol === 'proveedor' && userData.proveedor_id) {
                    const providerResponse = await providerService.getProviderById(userData.proveedor_id);

                    if (!providerResponse.error) {
                        setProviderInfo(providerResponse.data.data);
                    }
                }
            }
        } catch (error) {
            console.error('Error al cargar perfil:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'No se pudo cargar la información del perfil',
                timer: 3000,
                showConfirmButton: false
            });
        } finally {
            setLoading(false);
        }
    };

    const handleEditToggle = () => {
        if (editMode) {
            // Cancelar edición - restaurar valores originales
            setFormData({
                nombre: user.nombre || '',
                email: user.email || '',
                telefono: user.telefono || '',
                direccion: user.direccion || ''
            });
        }
        setEditMode(!editMode);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handlePasswordChange = (e) => {
        const { name, value } = e.target;
        setPasswordForm(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSaveProfile = async () => {
        try {
            // Validaciones
            if (!formData.nombre || !formData.email) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Campos requeridos',
                    text: 'El nombre y email son obligatorios',
                    timer: 2000,
                    showConfirmButton: false
                });
                return;
            }

            const updateData = {
                nombre: formData.nombre,
                email: formData.email
            };

            // Solo incluir teléfono y dirección si tienen valor
            if (formData.telefono) updateData.telefono = formData.telefono;
            if (formData.direccion) updateData.direccion = formData.direccion;

            const response = await authService.updateProfile(updateData);

            if (!response.error) {
                Swal.fire({
                    icon: 'success',
                    title: '¡Perfil actualizado!',
                    text: 'Tu información ha sido actualizada correctamente',
                    timer: 2000,
                    showConfirmButton: false
                });

                setEditMode(false);
                loadProfile(); // Recargar datos
            }
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.message || 'No se pudo actualizar el perfil',
                timer: 3000
            });
        }
    };

    const handleChangePassword = async () => {
        try {
            // Validaciones
            if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Campos requeridos',
                    text: 'Completa todos los campos de contraseña',
                    timer: 2000,
                    showConfirmButton: false
                });
                return;
            }

            if (passwordForm.newPassword.length < 6) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Contraseña débil',
                    text: 'La nueva contraseña debe tener al menos 6 caracteres',
                    timer: 2000,
                    showConfirmButton: false
                });
                return;
            }

            if (passwordForm.newPassword !== passwordForm.confirmPassword) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Contraseñas no coinciden',
                    text: 'La nueva contraseña y la confirmación deben ser iguales',
                    timer: 2000,
                    showConfirmButton: false
                });
                return;
            }

            const response = await authService.changePassword({
                currentPassword: passwordForm.currentPassword,
                newPassword: passwordForm.newPassword
            });

            if (!response.error) {
                Swal.fire({
                    icon: 'success',
                    title: '¡Contraseña actualizada!',
                    text: 'Tu contraseña ha sido cambiada correctamente',
                    timer: 2000,
                    showConfirmButton: false
                });

                // Limpiar formulario
                setPasswordForm({
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: ''
                });
            }
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.message || 'No se pudo cambiar la contraseña. Verifica tu contraseña actual.',
                timer: 3000
            });
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('es-MX', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <Layout>
                <div className="container">
                    <div className="profile-loading">
                        <div className="profile-loading-spinner"></div>
                        <span className="profile-loading-text">Cargando perfil...</span>
                    </div>
                </div>
            </Layout>
        );
    }

    if (!user) {
        return (
            <Layout>
                <div className="container">
                    <div className="profile-loading">
                        <i className="bi bi-exclamation-triangle" style={{ fontSize: '3rem', color: 'var(--color-warning)' }}></i>
                        <span className="profile-loading-text">No se pudo cargar la información del perfil</span>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="container">
                {/* Header del perfil */}
                <div className="profile-header">
                    <div className="profile-header-content">
                        <div className="profile-avatar-large">
                            {user.nombre?.charAt(0).toUpperCase() || 'U'}
                        </div>
                        <div className="profile-info">
                            <h1 className="profile-name">{user.nombre}</h1>
                            <p className="profile-role">
                                <i className={`bi ${user.rol === 'administrador' ? 'bi-shield-check' : 'bi-building'}`}></i>
                                {user.rol === 'administrador' ? 'Administrador' : 'Proveedor'}
                            </p>
                        </div>
                    </div>
                    <div className="profile-actions">
                        <button
                            className={`btn-edit-toggle ${editMode ? 'editing' : ''}`}
                            onClick={handleEditToggle}
                        >
                            <i className={`bi ${editMode ? 'bi-x-circle' : 'bi-pencil-square'}`}></i>
                            {editMode ? 'Cancelar' : 'Editar Perfil'}
                        </button>
                    </div>
                </div>

                {/* Información Personal */}
                <div className="card">
                    <h3 className="card-header">
                        <i className="bi bi-person-badge"></i> Información Personal
                    </h3>

                    <div className="profile-form">
                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">
                                    <i className="bi bi-person-fill"></i> Nombre Completo *
                                </label>
                                <input
                                    type="text"
                                    name="nombre"
                                    className={`form-input ${editMode ? 'editing' : ''}`}
                                    value={formData.nombre}
                                    onChange={handleInputChange}
                                    disabled={!editMode}
                                    placeholder="Tu nombre completo"
                                />
                            </div>

                            <div className="form-group">
                                <label className="form-label">
                                    <i className="bi bi-envelope-fill"></i> Correo Electrónico *
                                </label>
                                <input
                                    type="email"
                                    name="email"
                                    className={`form-input ${editMode ? 'editing' : ''}`}
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    disabled={!editMode}
                                    placeholder="correo@ejemplo.com"
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">
                                    <i className="bi bi-telephone-fill"></i> Teléfono
                                </label>
                                <input
                                    type="tel"
                                    name="telefono"
                                    className={`form-input ${editMode ? 'editing' : ''}`}
                                    value={formData.telefono}
                                    onChange={handleInputChange}
                                    disabled={!editMode}
                                    placeholder="8112345678"
                                    maxLength="10"
                                />
                            </div>

                            <div className="form-group">
                                <label className="form-label">
                                    <i className="bi bi-award-fill"></i> Rol
                                </label>
                                <input
                                    type="text"
                                    className="form-input"
                                    value={user.rol === 'administrador' ? 'Administrador' : 'Proveedor'}
                                    disabled
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label className="form-label">
                                <i className="bi bi-geo-alt-fill"></i> Dirección
                            </label>
                            <textarea
                                name="direccion"
                                className={`form-input ${editMode ? 'editing' : ''}`}
                                value={formData.direccion}
                                onChange={handleInputChange}
                                disabled={!editMode}
                                placeholder="Dirección completa"
                                rows="3"
                            />
                        </div>

                        {editMode && (
                            <div className="form-actions">
                                <button
                                    className="btn btn-primary"
                                    onClick={handleSaveProfile}
                                >
                                    <i className="bi bi-check-circle-fill"></i> Guardar Cambios
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Información de la Empresa (solo para proveedores) */}
                {user.rol === 'proveedor' && providerInfo && (
                    <div className="card">
                        <h3 className="card-header">
                            <i className="bi bi-building"></i> Información de la Empresa
                        </h3>
                        <div className="provider-info-grid">
                            <div className="info-item">
                                <div className="info-label">
                                    <i className="bi bi-file-text-fill"></i> RFC
                                </div>
                                <div className="info-value rfc-text">{providerInfo.rfc}</div>
                            </div>
                            <div className="info-item">
                                <div className="info-label">
                                    <i className="bi bi-briefcase-fill"></i> Razón Social
                                </div>
                                <div className="info-value">{providerInfo.razon_social}</div>
                            </div>
                            <div className="info-item">
                                <div className="info-label">
                                    <i className="bi bi-calendar-check-fill"></i> Días de Pago
                                </div>
                                <div className="info-value">{providerInfo.dias_pago} días</div>
                            </div>
                            <div className="info-item">
                                <div className="info-label">
                                    <i className="bi bi-toggle-on"></i> Estado
                                </div>
                                <div className="info-value">
                                    <span className={`status-badge ${providerInfo.activo ? 'status-aprobada' : 'status-rechazada'}`}>
                                        {providerInfo.activo ? 'Activo' : 'Inactivo'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Cambiar Contraseña */}
                <div className="card">
                    <h3 className="card-header">
                        <i className="bi bi-shield-lock-fill"></i> Cambiar Contraseña
                    </h3>
                    <div className="profile-form">
                        <div className="form-group">
                            <label className="form-label">
                                <i className="bi bi-key-fill"></i> Contraseña Actual *
                            </label>
                            <input
                                type="password"
                                name="currentPassword"
                                className="form-input"
                                value={passwordForm.currentPassword}
                                onChange={handlePasswordChange}
                                placeholder="Tu contraseña actual"
                            />
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">
                                    <i className="bi bi-shield-fill-check"></i> Nueva Contraseña *
                                </label>
                                <input
                                    type="password"
                                    name="newPassword"
                                    className="form-input"
                                    value={passwordForm.newPassword}
                                    onChange={handlePasswordChange}
                                    placeholder="Mínimo 6 caracteres"
                                    minLength="6"
                                />
                            </div>

                            <div className="form-group">
                                <label className="form-label">
                                    <i className="bi bi-shield-fill-check"></i> Confirmar Nueva Contraseña *
                                </label>
                                <input
                                    type="password"
                                    name="confirmPassword"
                                    className="form-input"
                                    value={passwordForm.confirmPassword}
                                    onChange={handlePasswordChange}
                                    placeholder="Repite la nueva contraseña"
                                    minLength="6"
                                />
                            </div>
                        </div>

                        <div className="password-requirements">
                            <div className="password-requirements-title">
                                <i className="bi bi-info-circle-fill"></i>
                                Requisitos de seguridad
                            </div>
                            <ul className="password-requirements-list">
                                <li><i className="bi bi-check-circle-fill"></i> Mínimo 6 caracteres</li>
                                <li><i className="bi bi-check-circle-fill"></i> Debe ser diferente a la contraseña actual</li>
                            </ul>
                        </div>

                        <div className="form-actions">
                            <button
                                className="btn btn-secondary"
                                onClick={handleChangePassword}
                            >
                                <i className="bi bi-shield-lock-fill"></i> Cambiar Contraseña
                            </button>
                        </div>
                    </div>
                </div>

                {/* Información de la Cuenta */}
                <div className="card">
                    <h3 className="card-header">
                        <i className="bi bi-info-circle-fill"></i> Información de la Cuenta
                    </h3>
                    <div className="account-info-grid">
                        <div className="info-item">
                            <div className="info-label">
                                <i className="bi bi-hash"></i> ID de Usuario
                            </div>
                            <div className="info-value">#{user.id}</div>
                        </div>
                        <div className="info-item">
                            <div className="info-label">
                                <i className="bi bi-calendar-event-fill"></i> Cuenta Creada
                            </div>
                            <div className="info-value">{formatDate(user.created_at)}</div>
                        </div>
                        {user.proveedor_id && (
                            <div className="info-item">
                                <div className="info-label">
                                    <i className="bi bi-building"></i> ID de Proveedor
                                </div>
                                <div className="info-value">#{user.proveedor_id}</div>
                            </div>
                        )}
                        <div className="info-item">
                            <div className="info-label">
                                <i className="bi bi-check-circle-fill"></i> Estado de la Cuenta
                            </div>
                            <div className="info-value">
                                <span className={`status-badge ${user.activo ? 'status-aprobada' : 'status-rechazada'}`}>
                                    {user.activo ? 'Activa' : 'Inactiva'}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};